import { ReactLocationDevtoolsPanel } from "@tanstack/react-location-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
// eslint-disable-next-line no-restricted-imports
import { useRouter } from "@tanstack/react-location";
import cx from "classnames";
import { Agentation } from "agentation";
import { faScrewdriverWrench } from "@fortawesome/pro-regular-svg-icons";

import { isDev } from "common/utils/debug";
import Icon from "common/components/Icon";
import { HOTKEYS, useGlobalHotkeys } from "common/hooks/hotkeys";
import Portal from "common/components/Portal";

import styles from "common/styles/components/DevTools.module.less";

const MIN_HEIGHT = 100;
const DEFAULT_HEIGHT = 400;

type PanelKind = "router" | "query";

type RenderTabContentProps = {
  isOpen: boolean;
  onChangeIsOpen: (isActive: boolean) => void;
};

type Tab = {
  label: string;
  panelKind: PanelKind;
  renderContent: (props: RenderTabContentProps) => ReactNode;
};

/**
 * Provides a UI to surface various devtools, this should be added to the react-tree with any
 * needed dev-tools appearing higher than this component.
 */
const TanstackDevTools = () => {
  const [activePannel, setActivePannel] = useState<PanelKind>("query");
  const [isOpen, setIsOpen] = useState(false);
  const [devToolsHeight, setDevtoolsHeight] = useState(DEFAULT_HEIGHT);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * Handle the user dragging the top of the panel up or down.
   * Simplified logic from react-query dev tools
   * https://github.com/TanStack/query/blob/52764b0d461cbfe3acd7c0840c0477b834a9a0ac/packages/react-query-devtools/src/devtools.tsx#L161
   * */
  const handleDragStart = (startEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const { height } = panel.getBoundingClientRect();
    const startY = startEvent.clientY;
    let newSize = 0;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      // prevent mouse selecting stuff with mouse drag
      moveEvent.preventDefault();
      newSize = height + startY - moveEvent.clientY;
      setDevtoolsHeight(Math.max(newSize, MIN_HEIGHT));
      if (newSize < MIN_HEIGHT) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };

    const stopHandlingMouseMove = () => {
      document.removeEventListener("mousemove", handleMouseMove, false);
      document.removeEventListener("mouseup", stopHandlingMouseMove, false);
    };

    document.addEventListener("mousemove", handleMouseMove, false);
    document.addEventListener("mouseup", stopHandlingMouseMove, false);
  };

  const tabs: Tab[] = [
    {
      label: "Query",
      panelKind: "query",
      renderContent: ({ onChangeIsOpen: onChangeIsActive }: RenderTabContentProps) => (
        <ReactQueryDevtoolsPanel
          onClose={() => onChangeIsActive(false)}
          style={{ height: devToolsHeight }}
        />
      ),
    },
    {
      label: "Router",
      panelKind: "router",
      // isOpen doesn't seem to actually hide the react location dev panel so
      // we have to handle conditional rendering ourselves
      renderContent: ({
        isOpen: isActive,
        onChangeIsOpen: onChangeIsActive,
      }: RenderTabContentProps) => (
        <ReactLocationDevtoolsPanel
          isOpen={isActive}
          setIsOpen={onChangeIsActive}
          handleDragStart={handleDragStart}
          useRouter={useRouter}
          style={{ height: devToolsHeight }}
        />
      ),
    },
  ];

  const activeTab = tabs.find((tab) => activePannel === tab.panelKind);

  if (!isOpen) {
    return (
      <button type="button" className={styles.openBtn} onClick={() => setIsOpen(true)}>
        <Icon icon={faScrewdriverWrench} />
      </button>
    );
  }

  return (
    <div className={styles.panels} style={{ height: "auto" }}>
      <div className={styles.head}>
        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              type="button"
              className={cx(styles.tab, activeTab?.panelKind === tab.panelKind && styles.active)}
              key={tab.label}
              onClick={() => setActivePannel(tab.panelKind)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button type="button" className={styles.closeBtn} onClick={() => setIsOpen(false)}>
          Close
        </button>
      </div>
      <div className={styles.content} ref={panelRef}>
        {activeTab?.renderContent({
          isOpen,
          onChangeIsOpen: () => setIsOpen(false),
        })}
      </div>
    </div>
  );
};

const DEVTOOLS_VISIBLE_STORAGE_KEY = "devtools-visible";

/** Matches the class name in DevTools.module.less */
const DEVTOOLS_VISIBLE_BODY_CLASS = "devtools-visible";

/**
 * Dev-only tooling cluster (Tanstack query/router panels + the Agentation feedback toolbar),
 * rendered to the bottom-right corner. Toggle the whole cluster on/off with the `mod+shift+d`
 * keyboard shortcut (`HOTKEYS.devtools.toggle`); when off, nothing renders.
 */
const DevTools = () => {
  const [isVisible, setIsVisible] = useLocalStorage(DEVTOOLS_VISIBLE_STORAGE_KEY, true);

  useGlobalHotkeys({
    shortcut: HOTKEYS.devtools.toggle,
    callback: (e) => {
      e.preventDefault(); // Prevent bookmarks from opening
      setIsVisible((prev) => !prev);
    },
  });

  useEffect(() => {
    // Log the toggle hotkey to help with debugging - only shown in development mode
    // eslint-disable-next-line no-console
    console.log("Toggle devtools with", HOTKEYS.devtools.toggle.key);
  }, []);

  // Hide Intercom launcher when DevTools buttons are visible to prevent overlap
  useEffect(() => {
    if (isVisible) {
      document.body.classList.add(DEVTOOLS_VISIBLE_BODY_CLASS);
    } else {
      document.body.classList.remove(DEVTOOLS_VISIBLE_BODY_CLASS);
    }

    return () => document.body.classList.remove(DEVTOOLS_VISIBLE_BODY_CLASS);
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <Portal>
        <TanstackDevTools />
      </Portal>
      {/* Agentation already renders in a portal */}
      <Agentation endpoint="http://localhost:4747" />
    </>
  );
};

export default isDev ? DevTools : () => null;
